import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Save, Percent, IndianRupee, FileText } from "lucide-react";

export default function SalaryRules() {
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState("");
  // Snapshot of the last-saved/loaded values per staffId, to detect edits.
  const [original, setOriginal] = useState({});

  // Normalize the editable fields so we can compare current vs. original.
  const rowKey = (r) => JSON.stringify({
    baseSalary: String(r.baseSalary ?? ""),
    commissionPct: String(r.commissionPct ?? ""),
    bonusAmount: String(r.bonusAmount ?? r.bonus ?? ""),
  });

  const load = () =>
    api.get("/payroll/salary-rules")
      .then((r) => {
        setRows(r.data);
        setOriginal((r.data || []).reduce((acc, row) => ({ ...acc, [row.staffId]: rowKey(row) }), {}));
      })
      .catch(() => toast.error("Failed"));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (sid, key, value) =>
    setRows((rs) => rs.map((r) => (r.staffId === sid ? { ...r, [key]: value } : r)));

  const isDirty = (r) => original[r.staffId] !== rowKey(r);

  const save = async (row) => {
    const base = Number(row.baseSalary);
    const pct = Number(row.commissionPct);
    const bonus = Number(row.bonusAmount || row.bonus || 0);
    if (isNaN(base) || base < 0) { toast.error("Base salary must be >= 0"); return; }
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Commission % must be 0-100"); return; }
    setSavingId(row.staffId);
    try {
      await api.put(`/payroll/salary-rules/${row.staffId}`, { baseSalary: base, commissionPct: pct, bonusAmount: bonus });
      toast.success(`Saved rules for ${row.name}`);
      // Row is now clean again — refresh its snapshot so Save disables.
      setOriginal((o) => ({ ...o, [row.staffId]: rowKey(row) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="space-y-8" data-testid="salary-rules-page">
      <div>
        <div className="overline mb-2">Compensation</div>
        <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3"><FileText className="w-8 h-8 text-emerald-500" /> Salary Rules</h1>
        <p className="text-emerald-muted mt-2 text-sm">Set a monthly base salary and commission % per staff. Payroll uses these values to compute payouts.</p>
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold">Staff</TableHead>
              <TableHead className="th-gold">Specialty</TableHead>
              <TableHead className="th-gold w-48">Base Salary (₹)</TableHead>
              <TableHead className="th-gold w-40">Commission %</TableHead>
              <TableHead className="th-gold w-40">Bonus (₹)</TableHead>
              <TableHead className="th-gold w-28"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-emerald-muted py-8">No staff configured.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.staffId} className="border-gold-subtle" data-testid={`salary-row-${r.staffId}`}>
                <TableCell className="text-white font-medium">{r.name}</TableCell>
                <TableCell className="text-emerald-muted">{r.designation}</TableCell>
                <TableCell>
                  <div className="relative">
                    <IndianRupee className="w-3.5 h-3.5 text-emerald-muted absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      type="number" min="0" step="1"
                      value={r.baseSalary}
                      onChange={(e) => setField(r.staffId, "baseSalary", e.target.value)}
                      className="bg-emerald-elev border-gold-subtle pl-8 h-10"
                      data-testid={`salary-base-${r.staffId}`}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="relative">
                    <Percent className="w-3.5 h-3.5 text-emerald-muted absolute right-3 top-1/2 -translate-y-1/2" />
                    <Input
                      type="number" min="0" max="100" step="0.5"
                      value={r.commissionPct}
                      onChange={(e) => setField(r.staffId, "commissionPct", e.target.value)}
                      className="bg-emerald-elev border-gold-subtle pr-8 h-10"
                      data-testid={`salary-commission-${r.staffId}`}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="relative">
                    <IndianRupee className="w-3.5 h-3.5 text-emerald-muted absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      type="number" min="0" step="1"
                      value={r.bonusAmount ?? r.bonus ?? ""}
                      onChange={(e) => setField(r.staffId, "bonusAmount", e.target.value)}
                      className="bg-emerald-elev border-gold-subtle pl-8 h-10"
                      data-testid={`salary-bonus-${r.staffId}`}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => save(r)}
                    disabled={savingId === r.staffId || !isDirty(r)}
                    data-testid={`save-salary-${r.staffId}`}
                    className="gold-button inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm disabled:opacity-60 disabled:cursor-not-allowed">
                    <Save className="w-3.5 h-3.5" /> {savingId === r.staffId ? "Saving…" : "Save"}
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
